"use client";

import { ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const INCLUDED_ELEMENTS = [{
  title: "Custom patios",
  description: "Flagstone, bluestone, and paver installations designed for proper Georgia yard drainage and low maintenance."
}, {
  title: "Outdoor kitchens",
  description: "Built-in gas grills, quartz or granite food-prep surfaces, outdoor refrigeration, sinks, and bar counters."
}, {
  title: "Screened porches",
  description: "Covered post-and-beam spaces with ceiling fans, electrical outlets, heaters, and bug-resistant screening."
}, {
  title: "Landscaping tie-ins",
  description: "Integrated masonry planters, retaining walls, decorative privacy screening, and sod restoration."
}, {
  title: "Deck and patio transitions",
  description: "Graded multi-level stair runs, flush threshold slider connections, and matching architectural handrails."
}];
export default function OutdoorLiving() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="outdoorlivingheroanswer" data-nav="light" ref={ref} className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main Answer & Included List */}
          <div className={`lg:col-span-7 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              Outdoor living spaces in Brookhaven
            </h1>

            {/* Direct 2-3 sentence answer */}
            <p className="mt-6 text-lg sm:text-xl text-foreground/80 leading-relaxed font-default">
              Outdoor living renovations in Brookhaven expand your home into durable, all-season backyard retreats built specifically for Georgia climate. These projects integrate covered architectural additions with open-air gathering areas that seamlessly mirror the style, stone, and trim of your existing home. From foundational grading and drainage to outdoor gas and plumbing lines, each space creates a comfortable transition from interior rooms to the landscape.
            </p>

            <div className="mt-10 pt-8 border-t border-border">
              <h2 className="text-xl sm:text-2xl font-serif font-medium text-foreground tracking-tight mb-6">
                What's included in an outdoor living build
              </h2>

              <ul className="space-y-4">
                {INCLUDED_ELEMENTS.map((item, i) => <li key={item.title} data-index={i} className="flex items-start gap-3.5">
                    <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary-foreground">
                      <Check className="size-3 text-secondary" strokeWidth={3} />
                    </span>
                    <div>
                      <span className="font-semibold text-foreground text-base">
                        {item.title}:
                      </span>{" "}
                      <span className="text-foreground/75 text-base">
                        {item.description}
                      </span>
                    </div>
                  </li>)}
              </ul>

              <div className="mt-8 pt-4">
                <Button asChild size="lg" className="rounded-md">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an outdoor living consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Realistic Photography Grid */}
          <div className={`lg:col-span-5 flex flex-col gap-6 transition-all duration-700 delay-150 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative overflow-hidden rounded-xl border border-border shadow-md bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_screened_porch_patio.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_screened_porch_patio.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_screened_porch_patio.webp-wvc-sizes" alt="Custom Brookhaven screened porch addition with stone outdoor fireplace and patio" className="w-full h-80 sm:h-96 object-cover object-center" loading="lazy" decoding="async" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="relative overflow-hidden rounded-lg border border-border shadow-sm bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_grill_station.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_grill_station.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_grill_station.png-wvc-sizes" alt="Built-in outdoor kitchen with granite prep counters and stainless steel grill" className="w-full h-44 sm:h-52 object-cover object-center" loading="lazy" decoding="async" />
              </div>

              <div className="relative overflow-hidden rounded-lg border border-border shadow-sm bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/stone_deck_patio_transition.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/stone_deck_patio_transition.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/stone_deck_patio_transition.png-wvc-sizes" alt="Stone step transition connecting home deck to flagstone backyard patio" className="w-full h-44 sm:h-52 object-cover object-center" loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}